import { QueryClient, QueryKey } from '@tanstack/react-query';

import {
  BatchReplyV3ItemState,
  batchReplyV3ItemResult,
  BatchReplyV3DetailDto,
} from '@/apis/batch-replies-v3.api';
import { REPLY_REQUEST_STATUS } from '@/consts/enum';
import { Reply } from '@/types/reply';
import { Review } from '@/types/review';

/**
 * 일괄댓글 v3 실시간 캐시 patch/reconcile.
 *
 * 원칙: 리뷰 카드의 상태(reply.request_status)가 SSOT. SSE delta / snapshot 이 오면
 * 무거운 리뷰 목록 refetch 없이 캐시(`['reviews_infinity']`, `['reviews']`)를 국소 patch.
 * - correlation key = review_id (delta.review_id null 이면 skip)
 * - monotonic 가드: terminal(완료/실패) > pending. stale delta 가 완료→대기로 역행하지 못하게.
 */

const TERMINAL = new Set<number>([
  REPLY_REQUEST_STATUS.COMPLETED,
  REPLY_REQUEST_STATUS.COMPLETED_BY_CEO,
  REPLY_REQUEST_STATUS.COMPLETED_BY_AI,
  REPLY_REQUEST_STATUS.MARKETING_COMPLETED,
  REPLY_REQUEST_STATUS.FAILED,
  REPLY_REQUEST_STATUS.MARKETING_FAILED,
  REPLY_REQUEST_STATUS.FAILED_WITHOUT_RETRY,
]);
const PENDING = new Set<number>([
  REPLY_REQUEST_STATUS.BATCH_PENDING,
  REPLY_REQUEST_STATUS.PENDING,
  REPLY_REQUEST_STATUS.DEFAULT,
  REPLY_REQUEST_STATUS.MARKETING_PENDING,
]);

/** 상태 우선순위: terminal(2) > pending(1) > 기타(0) */
const rank = (s: number): number => (TERMINAL.has(s) ? 2 : PENDING.has(s) ? 1 : 0);

/** 단일 review 를 patch. 변경 없으면 원본 참조 그대로 반환(리렌더 억제). */
function patchReview(
  review: Review,
  statusByReview: Map<string, number>,
  opts?: { inject?: boolean; comments?: Map<string, string> },
): Review {
  const nextStatus = statusByReview.get(review?.review_id);
  if (nextStatus === undefined) return review;

  const replies = review.replies ?? [];
  const reply = replies[0];

  if (!reply) {
    if (!opts?.inject) return review; // 붙일 reply 없음
    const synthetic: Reply = {
      reply_id: `optimistic-${review.review_id}`,
      date: '',
      comment: opts.comments?.get(review.review_id) ?? '',
      modifiable: false,
      request_status: nextStatus as REPLY_REQUEST_STATUS,
    };
    return { ...review, replies: [synthetic] };
  }

  // monotonic: 새 상태가 기존보다 낮으면 무시(완료→대기 역행 방지)
  if (rank(nextStatus) <= rank(reply.request_status)) return review;
  return {
    ...review,
    replies: [
      { ...reply, request_status: nextStatus as REPLY_REQUEST_STATUS },
      ...replies.slice(1),
    ],
  };
}

// 무한 쿼리 캐시: { pages: [{ data: Review[], ... }], pageParams }
type InfiniteReviewsCache = { pages?: { data?: Review[] }[]; pageParams?: unknown[] } | undefined;
// 단일 쿼리 캐시: { data: Review[], ... } (Paginate<Review>)
type ReviewsCache = { data?: Review[] } | undefined;

/**
 * review_id → request_status 맵을 두 리뷰 캐시(무한/단일)에 적용.
 * @param opts.inject reply 없는 카드(미답변)에 낙관적 합성 reply 주입(첫 전환용).
 */
function applyStatusMap(
  queryClient: QueryClient,
  statusByReview: Map<string, number>,
  opts?: { inject?: boolean; comments?: Map<string, string> },
): void {
  if (statusByReview.size === 0) return;

  // ── 무한 쿼리: ['reviews_infinity', <params>] ──
  queryClient.setQueriesData<InfiniteReviewsCache>({ queryKey: ['reviews_infinity'] }, (old) => {
    if (!old?.pages) return old;
    let touched = false;
    const pages = old.pages.map((page) => {
      if (!Array.isArray(page?.data)) return page;
      let pageTouched = false;
      const data = page.data.map((review) => {
        const next = patchReview(review, statusByReview, opts);
        if (next !== review) pageTouched = true;
        return next;
      });
      if (!pageTouched) return page;
      touched = true;
      return { ...page, data };
    });
    return touched ? { ...old, pages } : old;
  });

  // ── 단일 쿼리: ['reviews', <params>] ──
  // 주의: 같은 프리픽스의 summary 캐시 등은 old.data 가 Review[] 가 아니므로 방어적 skip.
  queryClient.setQueriesData<ReviewsCache>({ queryKey: ['reviews'] }, (old) => {
    if (!old || !Array.isArray(old.data)) return old;
    let touched = false;
    const data = old.data.map((review) => {
      const next = patchReview(review, statusByReview, opts);
      if (next !== review) touched = true;
      return next;
    });
    return touched ? { ...old, data } : old;
  });
}

/** 리뷰 캐시(무한/단일)에서 review_id로 Review를 찾음 — island 라벨 조인용 */
export const findCachedReview = (
  queryClient: QueryClient,
  reviewId: string,
): Review | undefined => {
  const infinite = queryClient.getQueriesData<InfiniteReviewsCache>({
    queryKey: ['reviews_infinity'],
  });
  for (const [, data] of infinite) {
    if (!data?.pages) continue;
    for (const page of data.pages) {
      const found = page.data?.find((r) => r.review_id === reviewId);
      if (found) return found;
    }
  }
  const single = queryClient.getQueriesData<ReviewsCache>({ queryKey: ['reviews'] });
  for (const [, data] of single) {
    if (!Array.isArray(data?.data)) continue;
    const found = data.data.find((r) => r.review_id === reviewId);
    if (found) return found;
  }
  return undefined;
};

/** 단일 SSE delta 적용 (review_id null 이면 skip) */
export const patchReviewReplyStatus = (
  queryClient: QueryClient,
  delta: { review_id: string | null; request_status: number },
): void => {
  if (!delta.review_id) return;
  applyStatusMap(queryClient, new Map([[delta.review_id, delta.request_status]]));
};

/** snapshot items[] 일괄 reconcile (재연결/백스톱/terminal) */
export const reconcileFromSnapshot = (
  queryClient: QueryClient,
  items: BatchReplyV3ItemState[],
): void => {
  const map = new Map<string, number>();
  for (const it of items) {
    if (it.review_id) map.set(it.review_id, it.request_status);
  }
  applyStatusMap(queryClient, map);
};

// ── 현황/결과 화면 detail 캐시 라이브 갱신 (AFFiNE: SSE delta 낙관적 patch + snapshot fallback) ──
// detail은 한 번만 fetch(본문). 건별 status는 delta로 detail 캐시를 직접 patch → 화면이 폴링 없이 라이브.
// review_id 기준, terminal(성공/실패) > pending 단조 가드로 stale delta 역행 방지.

/** detail reply_items 의 status 우선순위: terminal(2) > pending(1) */
const detailRank = (status: number | string): number => {
  const r = batchReplyV3ItemResult(status);
  return r === 'pending' ? 1 : 2;
};

/** detail 캐시가 존재할 때만, review_id → request_status 맵으로 reply_items[].status 를 단조 patch */
const applyDetailStatusMap = (
  queryClient: QueryClient,
  detailKey: QueryKey,
  statusByReview: Map<string, number>,
): void => {
  if (statusByReview.size === 0) return;
  queryClient.setQueryData<BatchReplyV3DetailDto | undefined>(detailKey, (old) => {
    if (!old || !Array.isArray(old.reply_items)) return old;
    let touched = false;
    const reply_items = old.reply_items.map((it) => {
      const next = statusByReview.get(it.review_id);
      if (next === undefined || detailRank(next) <= detailRank(it.status)) return it;
      touched = true;
      return { ...it, status: next };
    });
    return touched ? { ...old, reply_items } : old;
  });
};

/** 단일 SSE delta 로 detail 캐시 patch (현황 화면 라이브) */
export const patchDetailReplyStatus = (
  queryClient: QueryClient,
  detailKey: QueryKey,
  delta: { review_id: string | null; request_status: number },
): void => {
  if (!delta.review_id) return;
  applyDetailStatusMap(queryClient, detailKey, new Map([[delta.review_id, delta.request_status]]));
};

/** snapshot items[] 로 detail 캐시 일괄 reconcile (놓친 delta 보정 fallback) */
export const reconcileDetailFromSnapshot = (
  queryClient: QueryClient,
  detailKey: QueryKey,
  items: BatchReplyV3ItemState[],
): void => {
  const map = new Map<string, number>();
  for (const it of items) {
    if (it.review_id) map.set(it.review_id, it.request_status);
  }
  applyDetailStatusMap(queryClient, detailKey, map);
};

/**
 * create 200 직후: 접수된 review 들을 BATCH_PENDING(9)로 승격.
 * 미답변 카드(replies:[])는 낙관적 합성 reply 주입으로 즉시 "대기중" 표시.
 */
export const promoteToPending = (
  queryClient: QueryClient,
  reviewIds: string[],
  comments?: Map<string, string>,
): void => {
  const map = new Map<string, number>(
    reviewIds.map((id) => [id, REPLY_REQUEST_STATUS.BATCH_PENDING]),
  );
  applyStatusMap(queryClient, map, { inject: true, comments });
};
